import React, { useEffect } from "react";
import { Routes, Route, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import useInactivityTracker from '../hooks/useInactivityTracker';
import useCartRestoration from '../hooks/useCartRestoration';

// Import pages
import HomePage from '../pages/HomePage';
import Home from '../pages/Home';
import AuthSignIn from '../pages/auth/SignIn';
import AuthSignUp from '../pages/auth/SignUp';
import Categories from '../pages/CategoriesPage';
import Category from '../pages/Category';
import Store from '../pages/StorePage';
import StoreDetails from '../pages/StoreDetailsPage';
import Product from '../pages/ProductPage';
import Search from '../pages/SearchPage';
import Profile from '../pages/Profile';
import MyOrders from '../pages/MyOrders';
import OrderDetails from '../pages/OrderDetails';
import Checkout from '../pages/Checkout';
import Wallet from '../pages/Wallet';
import Wishlist from '../pages/Wishlist';
import Address from '../pages/Address';
import Coupons from '../pages/Coupons';
import Settings from '../pages/Settings';
import AboutUs from '../pages/AboutUs';
import PrivacyPolicy from '../pages/PrivacyPolicy';
import TermsAndConditions from '../pages/TermsAndConditions';
import RefundPolicy from '../pages/RefundPolicy';
import CancellationPolicy from '../pages/CancellationPolicy';
import ShippingPolicy from '../pages/ShippingPolicy';
import HelpAndSupport from '../pages/HelpAndSupport';
import TrackOrder from '../pages/TrackOrder';
import Campaigns from '../pages/Campaigns';
import CampaignDetails from '../pages/CampaignDetails';
import FlashSales from '../pages/FlashSales';
import Interest from '../pages/Interest';
import LoyaltyPoints from '../pages/LoyaltyPoints';
import ReferralCode from '../pages/ReferralCode';
import Chatting from '../pages/Chatting';
import RateAndReview from '../pages/RateAndReview';
import Review from '../pages/Review';
import SubCategory from '../pages/SubCategory';
import Order from '../pages/Order';
import ForgotPassword from '../pages/ForgotPassword';
import ParcelDeliveryInfo from '../pages/ParcelDeliveryInfo';
import CustomNoSSR from '../pages/CustomNoSSR';
import Maintainance from '../pages/maintainance';
import NotFound from '../pages/404';
import ServerError from '../pages/500';
import PopularStorePage from '../pages/store/popular/PopularStorePage';
import LatestStorePage from '../pages/store/latest/LatestStorePage';

// Route Guard Components
import AuthGuard from './route-guard/AuthGuard';
import ZoneGuard from './route-guard/ZoneGuard';

// Component to handle redirection for order tracking from profile
const OrderTrackingRedirect = ({ children }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const tab = searchParams.get('tab');
  const orderId = searchParams.get('orderId');

  useEffect(() => {
    if (tab === 'track-order' && orderId) {
      navigate(`/track-order/${orderId}`, { replace: true });
    }
  }, [tab, orderId, navigate]);

  return children;
};

const InactivityWrapper = () => {
  const { isInactive, resetActivityTimer } = useInactivityTracker(30 * 60 * 1000); // 30 minutes
  const location = useLocation();
  
  // Initialize cart restoration
  useCartRestoration();

  // Show inactivity message only if user is inactive for 1 hour and on root route
  if (isInactive && location.pathname === '/') {
    return (
      <div onClick={resetActivityTimer}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          cursor: 'pointer'
        }}>
          Click to continue...
        </div>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public Routes - Root route should not be zone-protected */}
      <Route path="/" element={<HomePage />} />
      <Route path="/home" element={<ZoneGuard><Home /></ZoneGuard>} />
      <Route path="/auth/sign-in" element={<AuthSignIn />} />
      <Route path="/auth/sign-up" element={<AuthSignUp />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/about-us" element={<AboutUs />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
      <Route path="/refund-policy" element={<RefundPolicy />} />
      <Route path="/cancellation-policy" element={<CancellationPolicy />} />
      <Route path="/shipping-policy" element={<ShippingPolicy />} />
      <Route path="/help-and-support" element={<HelpAndSupport />} />
      <Route path="/parcel-delivery-info" element={<ParcelDeliveryInfo />} />
      <Route path="/custom-no-ssr" element={<CustomNoSSR />} />
      <Route path="/maintainance" element={<Maintainance />} />
      
      {/* Zone Protected Routes */}
      <Route path="/categories" element={<ZoneGuard><Categories /></ZoneGuard>} />
      <Route path="/category/:id" element={<ZoneGuard><Category /></ZoneGuard>} />
      <Route path="/sub-category/:id" element={<ZoneGuard><SubCategory /></ZoneGuard>} />
      <Route path="/store" element={<ZoneGuard><Store /></ZoneGuard>} />
      <Route path="/store/latest" element={<ZoneGuard><LatestStorePage /></ZoneGuard>} />
      <Route path="/store/popular" element={<ZoneGuard><PopularStorePage /></ZoneGuard>} />
      <Route path="/store/:id" element={<ZoneGuard><StoreDetails /></ZoneGuard>} />
      <Route path="/product/:id" element={<ZoneGuard><Product /></ZoneGuard>} />
      <Route path="/search" element={<ZoneGuard><Search /></ZoneGuard>} />
      <Route path="/campaigns" element={<ZoneGuard><Campaigns /></ZoneGuard>} />
      <Route path="/campaigns/:id" element={<ZoneGuard><CampaignDetails /></ZoneGuard>} />
      <Route path="/flash-sales" element={<ZoneGuard><FlashSales /></ZoneGuard>} />
      
      {/* Auth Protected Routes */}
      <Route path="/profile" element={<OrderTrackingRedirect><AuthGuard><Profile /></AuthGuard></OrderTrackingRedirect>} />
      <Route path="/profile/profile" element={<OrderTrackingRedirect><AuthGuard><Profile /></AuthGuard></OrderTrackingRedirect>} />
      <Route path="/my-orders" element={<AuthGuard><MyOrders /></AuthGuard>} />
      <Route path="/my-orders/profile" element={<OrderTrackingRedirect><AuthGuard><Profile /></AuthGuard></OrderTrackingRedirect>} />
      <Route path="/order" element={<AuthGuard><Order /></AuthGuard>} />
      <Route path="/order-details/:id" element={<AuthGuard><OrderDetails /></AuthGuard>} />
      <Route path="/checkout" element={<AuthGuard from="checkout"><Checkout /></AuthGuard>} />
      <Route path="/wallet" element={<AuthGuard><Wallet /></AuthGuard>} />
      <Route path="/wishlist" element={<AuthGuard><Wishlist /></AuthGuard>} />
      <Route path="/address" element={<AuthGuard><Address /></AuthGuard>} />
      <Route path="/coupons" element={<AuthGuard><Coupons /></AuthGuard>} />
      <Route path="/settings" element={<AuthGuard><Settings /></AuthGuard>} />
      <Route path="/track-order" element={<TrackOrder />} />
      <Route path="/track-order/:id" element={<TrackOrder />} />
      <Route path="/interest" element={<AuthGuard><Interest /></AuthGuard>} />
      <Route path="/loyalty-points" element={<AuthGuard><LoyaltyPoints /></AuthGuard>} />
      <Route path="/referral-code" element={<AuthGuard><ReferralCode /></AuthGuard>} />
      <Route path="/chatting" element={<AuthGuard><Chatting /></AuthGuard>} />
      <Route path="/rate-and-review/:id" element={<AuthGuard><RateAndReview /></AuthGuard>} />
      <Route path="/review/:id" element={<AuthGuard><Review /></AuthGuard>} />
      
      {/* Error Routes */}
      <Route path="/500" element={<ServerError />} />
      <Route path="/404" element={<NotFound />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default InactivityWrapper;